import SideBar from "../../components/AdminDashboard/SideBar";
import "../../styles/AdminDashboard/Settings.scss";
import { useEffect, useRef, useState } from "react";
import {
  getUserInfo,
  updateAdminInfo,
} from "../../apis/General/users";
import { useSelector } from "react-redux";
import { deleteAdmin, getAdmins } from "../../apis/AdminDashboard/users";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";
import Search from "../../components/AdminDashboard/Search";

const AccountManagementSettings = () => {
  const auth = useSelector((state) => state.auth);
  const navigate = useNavigate();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [admins, setAdmins] = useState([]); 
  const mediaRef = useRef(null);

  // Fetch current user info
  const fetchUserInfo = async () => {
    setLoading(true);
    try {
      const response = await getUserInfo(auth?.userInfo?.id, auth?.token);
      setFirstName(response?.firstName || "");
      setLastName(response?.lastName || "");
      setEmail(response?.email || "");
      console.log("Fetched user info:", response);
    } catch (err) {
      console.error("Failed to fetch user info:", err);
    }
    setLoading(false);
  };

  // Fetch admins
  const fetchAdmins = async () => {
    try {
      const data = await getAdmins(auth?.token);
      console.log("Fetched admins:", data);
      setAdmins(data);
    } catch (err) {
      console.error("Failed to fetch admins:", err);
      setAdmins([]);
    }
  };

  useEffect(() => {
    fetchUserInfo();
    fetchAdmins();
  }, []);

  // ✅ Fixed remove admin function
 const handleRemoveAdmin = async (adminId) => {
  if (!adminId) {
    toast.error("Missing admin ID");
    return;
  }

  setLoading(true);
  console.log("Admin ID:", adminId);
  console.log("Token:", auth?.token);

  try {
    const deleteRes = await deleteAdmin(auth?.token, adminId); // ✅ already returns res.data
    console.log("Delete response:", deleteRes);

    if (deleteRes?.status === "success") {
      toast.success(deleteRes?.message || "Admin removed successfully");
       fetchAdmins(); // 🔄 uncomment to refresh list if needed
    } else {
      toast.error(deleteRes?.message || "Failed to remove admin");
    }
  } catch (err) {
    console.error("Delete admin error:", err);
    toast.error(err?.response?.data?.message || "Something went wrong while deleting admin");
  } finally {
    setLoading(false);
  }
};

  // Update admin profile
  const handleUpdateAdmin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await updateAdminInfo(firstName, lastName, email, "", auth?.token);
      toast.success("Admin info updated successfully");
    } catch (err) {
      toast.error("Failed to update admin info");
    }
    setLoading(false);
  };

  const cards = [
    {
      title: "Profile",
      description: "Update your personal details and picture",
      border: "border-[#E5E5E5]",
      Links: "/admin/admin-profile",
    },
    {
      title: "Password & Security",
      description: "See your password and update your password",
      border: "border-[#E5E5E5]",
      Links: "/admin/admin-password-and-security",
    },
    {
      title: "Team Access",
      description:
        "Control team accounts, assign roles, update details, and manage access securely.",
      border: "border-[#2C9963]",
      Links: "/admin/account-management-settings",
    },
  ];

  return (
    <div className="admin_settings">
      <div className="sidebar_container">
        <SideBar />
      </div>

      <div className="main">
        <Search />

        <h2 className="text-[24px] font-semibold leading-[29px] text-[#101828] mb-8 mt-20">
          Settings
        </h2>

        {/* Cards */}
        <div className="flex gap-[33px]">
          {cards.map((card, idx) => (
            <Link to={card.Links} key={idx}>
              <div
                className={`relative w-[356px] h-[167px] border ${card.border} rounded-[12px] p-6`}
              >
                <div className="absolute left-6 top-[72px] flex flex-col gap-2 w-[314px]">
                  <h3 className="text-[24px] font-medium leading-[29px] text-[#101828]">
                    {card.title}
                  </h3>
                  <p className="text-[14px] leading-[17px] text-[#98A2B3]">
                    {card.description}
                  </p>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Manage Roles */}
        <div className="border border-[#667185] rounded-2xl w-[1150px] bg-white mt-10 p-12">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-lg font-semibold text-[#101828]">Manage Roles</h2>
              <p className="text-[12px] text-[#667185] mb-5">
                Control team accounts, assign roles, update details, and manage
                access securely.
              </p>
            </div>
            <Link
              to={"/admin/add-admin"}
              className="flex items-center gap-2 px-12 py-6 rounded-xl bg-gradient-to-r from-[#038B47] to-[#00AED9] text-white text-[16px] font-semibold"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M19 13H13V19H11V13H5V11H11V5H13V11H19V13Z"
                  fill="white"
                />
              </svg>
              Add Admin
            </Link>
          </div>

          <div className="border rounded-md overflow-hidden p-5">
            {/* Column Labels */}
            <div className="px-6 py-4 bg-[#FAFAFA] text-[16px] font-medium text-[#101828]">
              <div className="flex items-center gap-96">
                <span>Name</span>
                <span className="ml-40">Email</span>
                <span>Role</span>
                <span>Actions</span>
              </div>
            </div>

            {/* Rows */}
            {admins?.data?.data?.map((user, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between px-6 py-4 border-t border-[#E5E5E5] text-[15px] text-[#101828] hover:bg-gray-50 transition-colors duration-200"
              >
                {/* Name */}
                <div className="flex items-center w-48 gap-2 font-medium">
                  <p>{user.firstName}</p>
                  <p>{user.lastName}</p>
                </div>

                {/* Email */}
                <div className="w-64 text-[#00AED9]">{user.email}</div>

                {/* Role */}
                <div className="w-32 text-gray-700 capitalize">{user.role}</div>

                {/* Actions */}
                <div className="flex gap-3 w-48 justify-end">
                  <Link to={`/admin/edith-admin/${user.id}`}>
                    <button className="px-4 py-2 bg-[#0D924F] hover:bg-[#0b7c42] text-white text-[14px] font-medium rounded-lg transition-all duration-200">
                      Edit
                    </button>
                  </Link>
                  <button
                    onClick={() => handleRemoveAdmin(user.id)}
                    className="px-4 py-2 border border-gray-300 text-[14px] font-medium rounded-lg hover:bg-gray-100 transition-all duration-200"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountManagementSettings;
